import { SparkSuspense } from '@sparklayer-io/spark-dash-core/components/spark-suspense';
import {
  useHandlers,
  withPageProviders,
} from '@sparklayer-io/spark-dash-pages/context/page-context';
import { Alert, LearnMoreLink, Page } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { CustomerDetailsCard } from '../components/customer-details-card';
import { CustomerRoleCard } from '../components/customer-role-card';
import { CustomerSummaryCard } from '../components/customer-summary-card';
import { SalesAgentUpsellAlert } from '../components/sales-agent-upsell-alert';
import { CUSTOMERS_SYNC_PATH, DOCS_URLS, I18N_DATA_PAGE, I18N_PAGE } from '../constants';
import { useCustomerLookupQuery } from '../hooks/use-customer-lookup-query';
import { useSalesAgentsUnavailable } from '../hooks/use-sales-agents-unavailable';
import type { Customer } from '../types';

export const CustomerSyncDataPage = withPageProviders(() => {
  const { navigationHandler } = useHandlers();
  const { identifier } = navigationHandler.params();

  const { data: found, isLoading, isError, refetch } = useCustomerLookupQuery(identifier);
  const salesAgentsUnavailable = useSalesAgentsUnavailable();
  // Local override so a role/group save reflects immediately, without waiting on a refetch.
  const [customer, setCustomer] = useState<Customer | undefined>(found?.customer);

  useEffect(() => {
    setCustomer(found?.customer);
  }, [found]);

  return (
    <Page
      title={t(`${I18N_DATA_PAGE}.title`)}
      description={t(`${I18N_DATA_PAGE}.description`, { identifier: identifier ?? '' })}
      backUrl={CUSTOMERS_SYNC_PATH}
    >
      {isError ? (
        <Alert variant="warning" headingLevel="h3" title={t(`${I18N_PAGE}.not_found.title`)}>
          <span>
            {t(`${I18N_PAGE}.not_found.description`, { identifier: identifier ?? '' })}{' '}
            <LearnMoreLink link={DOCS_URLS.troubleshooting} />
          </span>
        </Alert>
      ) : (
        <SparkSuspense
          isLoading={isLoading}
          isError={false}
          errorMessage={t('errors:generic')}
          onReload={() => void refetch()}
        >
          {customer && found && (
            <>
              <CustomerSummaryCard customer={customer} />
              {salesAgentsUnavailable ? (
                <SalesAgentUpsellAlert />
              ) : (
                !customer.parent_customer && (
                  <CustomerRoleCard
                    key={customer.id}
                    customer={customer}
                    identifier={found.identifier}
                    lookupBy={found.lookupBy}
                    onSaved={setCustomer}
                  />
                )
              )}
              <CustomerDetailsCard customer={customer} />
            </>
          )}
        </SparkSuspense>
      )}
    </Page>
  );
});
